import React, { useMemo, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { Reservation } from '@luxuryescapes/contract-svc-reservation';

import RatePlanList from '~/components/Common/RatePlanList';

import {
  RATE_PLAN_PRODUCT_LABELS,
  RATE_PLAN_STATUS_LABELS,
  compareRatePlans,
  getRatePlanProductGroup,
  getRatePlanStatusGroup,
} from '~/utils/ratePlanGrouping';

import RatePlanStatusSections from './RatePlanStatusSections';

type Props = React.ComponentProps<typeof RatePlanList>;

export default function GroupedRatePlanList(props: Props) {
  const [search, setSearch] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const filteredPlans = useMemo(
    () =>
      props.ratePlans.filter((plan) => {
        const matchesSearch = `${plan.name} ${plan.rate_plan_code ?? ''}`
          .toLocaleLowerCase()
          .includes(search.trim().toLocaleLowerCase());
        return matchesSearch && (statusFilter === 'all' || getRatePlanStatusGroup(plan.status) === statusFilter);
      }),
    [props.ratePlans, search, statusFilter],
  );

  const groups = useMemo(() => {
    return Object.entries(RATE_PLAN_PRODUCT_LABELS)
      .map(([key, label]) => ({
        key,
        label,
        plans: filteredPlans
          .filter((plan) => getRatePlanProductGroup(plan.product_type) === key)
          .sort(compareRatePlans),
      }))
      .filter((group) => group.plans.length > 0);
  }, [filteredPlans]);

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Rate plan status is separate from offer status. An expired Flash offer does not require its rate plan to be
        hidden. Hiding a rate plan can affect customers viewing or managing existing bookings.
      </Alert>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          label="Search rate plans"
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: { xs: '100%', md: 320 } }}
        />
        <TextField
          select
          label="Rate plan status"
          size="small"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">All statuses</MenuItem>
          {Object.entries(RATE_PLAN_STATUS_LABELS).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={<Switch size="small" checked={showDetails} onChange={(_, checked) => setShowDetails(checked)} />}
          label="Show details"
        />
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {filteredPlans.length} of {props.ratePlans.length} rate plans
        </Typography>
      </Stack>
      {groups.map((group) => (
        <Accordion
          key={group.key}
          defaultExpanded
          disableGutters
          elevation={0}
          sx={{ border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', flexWrap: 'wrap', rowGap: 1 }}>
              <Typography variant="h6">{group.label} rate plans</Typography>
              <Chip label={group.plans.length} size="small" />
              <Box sx={{ flex: 1 }} />
              {Object.entries(RATE_PLAN_STATUS_LABELS).map(([status, label]) => {
                const count = group.plans.filter((plan) => getRatePlanStatusGroup(plan.status) === status).length;
                return (
                  count > 0 && (
                    <Chip
                      key={status}
                      size="small"
                      label={`${count} ${label.toLowerCase()}`}
                      color={status === 'active' ? 'success' : status === 'hidden' ? 'warning' : 'default'}
                    />
                  )
                );
              })}
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <RatePlanStatusSections
              {...props}
              groupLabel={group.label}
              expandMatches={statusFilter !== 'all' || !!search.trim()}
              ratePlans={group.plans as Array<Reservation.RatePlan>}
              allRatePlans={props.allRatePlans ?? props.ratePlans}
              showDetails={showDetails}
            />
          </AccordionDetails>
        </Accordion>
      ))}
      {groups.length === 0 && (
        <Stack spacing={1} alignItems="center" sx={{ py: 4 }}>
          <Typography>No rate plans match these filters.</Typography>
          {(search || statusFilter !== 'all') && (
            <Button
              onClick={() => {
                setSearch('');
                setStatusFilter('all');
              }}
            >
              Clear search and status filter
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}
